import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

import { ChatStreamingContext } from "src/reusable/Chat/ChatStreaming/context/ChatStreamingContext";
import type { StreamingState } from "src/reusable/Chat/ChatStreaming/stores/StreamingStore";
import type { StoreApi } from "zustand";
import {
  citedChunkIdsInOrder,
  overlayLiveCitedChunks,
  overlayLiveSearched,
  type LiveChunkSource,
} from "./clientChunkSources";
import {
  citedExternalIdsInOrder,
  collectWebExternalsFromToolTexts,
  collectWebSearchQueriesFromToolTexts,
  concatTextParts,
  overlayLiveWebExternals,
  webSearchToolTextsSnapshot,
  type ClientWebExternal,
  type ClientWebSearchQuery,
} from "./clientWebExternals";
import {
  EMPTY_PROVENANCE,
  type AssistantResponseSources,
  type ProvenanceSearchedEntry,
} from "../../types/provenance";

export interface LiveWebExternals {
  externals: Map<number, ClientWebExternal>;
  citedIdsInOrder: number[];
  queries: ClientWebSearchQuery[];
  /** Chunk-band cite ids in the streamed text, for the cited-chunk overlay
   * ({@link ./clientChunkSources}) — the chunk twin of citedIdsInOrder. */
  citedChunkIds: number[];
  /** Searched-tier entries streamed at tool completion (#20423), read from
   * the stream store where the metadata handler put them. */
  searchedEntries: ProvenanceSearchedEntry[];
  /** True while this dialogue's stream is still producing the current turn
   * (`isTurnActive || isLoading`, the same pair
   * {@link ../../services/ProvenanceService#useProvenanceTurnRefresh} treats as
   * "turn not settled yet"). Tells a still-streaming turn apart from a cached
   * `turn_settled` payload describing the PREVIOUS one. */
  turnActive: boolean;
}

const EMPTY: LiveWebExternals = {
  externals: new Map(),
  citedIdsInOrder: [],
  queries: [],
  citedChunkIds: [],
  searchedEntries: [],
  turnActive: false,
};

/**
 * The provenance model every live surface derives from: the backend payload
 * with this dialogue's still-streaming sources overlaid — web externals the
 * moment the user approves the search, chunk sources the moment the answer
 * cites them. SourcesTab (panel) and AASidebar (badge) hand-built this
 * composition separately and drifted (the badge dropped `live.queries`);
 * deriving both from one helper keeps panel and badge in agreement.
 */
export function overlayLiveProvenance(
  provenance: AssistantResponseSources | null | undefined,
  live: LiveWebExternals,
  liveSources: LiveChunkSource[] | undefined,
): AssistantResponseSources {
  // Once the turn has SETTLED, the backend's Searched tier is final and wins
  // outright — the live rows are DISCARDED, not merged (PR #2248 review).
  // Merging them back was unsound because ownership is matched on a key
  // containing `query_text`, exactly the field the permission filter blanks:
  // a partially-visible row came back trimmed under a different key (so the
  // panel showed the trimmed row AND the original, query text and all), a
  // fully-hidden row was dropped by the server and the live twin kept it on
  // screen for good, and a permission-lookup DB error (server hides
  // everything as the safe default) refilled the whole tier unfiltered.
  // Discarding makes the tier match what the server decided, empty included.
  //
  // `turnActive` is what keeps this from disabling the live tier for the rest
  // of the dialogue: while a NEW turn streams, the cached payload is still the
  // previous turn's settled one, and that turn's `turn_settled` says nothing
  // about this one.
  const settled = provenance?.turn_settled === true && !live.turnActive;
  // Searched first: the web overlay dedups its client-derived query rows
  // against provenance.searched, so a backend-streamed web_search entry must
  // already be folded in for its richer twin to win.
  return overlayLiveCitedChunks(
    overlayLiveWebExternals(
      overlayLiveSearched(
        provenance ?? EMPTY_PROVENANCE,
        settled ? undefined : live.searchedEntries,
      ),
      live.externals,
      live.citedIdsInOrder,
      live.queries,
    ),
    liveSources,
    live.citedChunkIds,
  );
}

/**
 * LIVE web-search externals of a dialogue, read straight from its streaming
 * store (#20423 A2) — the same store `WebSearchApproval` stitches the approved
 * results into, so they're available the moment the user approves: no
 * `/messages/provenance` fetch, no poll, no reload. Feeds the Sources panel /
 * badge the way `AAAssistantText` feeds the inline markers.
 *
 * The `useSyncExternalStore` snapshot is a string (tool texts + cited ids), so
 * the per-token store updates of a streaming answer only re-render subscribers
 * when a search result or a new cite marker actually appears.
 */
export function useLiveWebExternals(
  dialogueId?: number | null,
): LiveWebExternals {
  const streamManager = useContext(ChatStreamingContext);
  const liveDialogueId =
    dialogueId != null && Number.isFinite(dialogueId) ? dialogueId : null;

  // Hosts own their stores and register them by dialogueId; the registry
  // is the only lookup left.
  const resolveStore = useCallback(
    () =>
      streamManager && liveDialogueId != null
        ? streamManager.getState().resolve(liveDialogueId)
        : null,
    [streamManager, liveDialogueId],
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!streamManager || liveDialogueId == null) return () => {};
      let current: StoreApi<StreamingState> | null = null;
      let unsubStream: (() => void) | undefined;
      // Re-resolve on each manager change: the store may not exist yet, and
      // the registry can later answer with a different object.
      const attach = () => {
        const next = resolveStore();
        if (next === current) return;
        unsubStream?.();
        current = next;
        unsubStream = next ? next.subscribe(onChange) : undefined;
      };
      attach();
      const unsubManager = streamManager.subscribe(() => {
        attach();
        onChange();
      });
      return () => {
        unsubStream?.();
        unsubManager();
      };
    },
    [streamManager, liveDialogueId, resolveStore],
  );

  // getSnapshot runs per store update (i.e. per streamed token) for every
  // subscriber, and the regex scans below walk the full answer text each time
  // — O(answer^2) per subscriber over a stream. The derived snapshot is cached
  // on the concatenated text (cheap length + identity short-circuit) so the
  // scans only re-run when the text actually changed.
  const snapshotCache = useRef<{ text: string; value: string } | null>(null);
  // Serialized searchedEntries, cached on ARRAY IDENTITY: setSearchedEntries
  // returns the same array when an emit brings nothing new, and getSnapshot
  // runs per streamed token — so the stringify must not re-run per token
  // (PR #2248 review).
  const searchedJsonCache = useRef<{
    arr: ProvenanceSearchedEntry[];
    json: string;
  } | null>(null);
  const snapshot = useSyncExternalStore(subscribe, () => {
    if (!streamManager || liveDialogueId == null) return "";
    const state = resolveStore()?.getState();
    const dialogue = state?.dialogue;
    // Streamed searched entries can precede any answer text (the retrieval
    // phase runs first), so they alone are enough to produce a snapshot.
    const searched = (state?.searchedEntries ??
      []) as ProvenanceSearchedEntry[];
    const turnActive =
      state?.isTurnActive === true || state?.isLoading === true;
    let searchedJson = "";
    if (searched.length) {
      if (searchedJsonCache.current?.arr === searched) {
        searchedJson = searchedJsonCache.current.json;
      } else {
        searchedJson = JSON.stringify(searched);
        searchedJsonCache.current = { arr: searched, json: searchedJson };
      }
    }
    if (!dialogue?.length && !searchedJson) return "";
    const text = dialogue?.length ? concatTextParts(dialogue) : "";
    const toolTexts = dialogue?.length
      ? webSearchToolTextsSnapshot(dialogue)
      : null;
    const cacheKey = `${text.length}:${text}:${toolTexts ?? ""}:${searchedJson}:${turnActive}`;
    if (snapshotCache.current?.text === cacheKey) {
      return snapshotCache.current.value;
    }
    // Chunk cites don't need web tool texts — a Nuclearn_Search turn has
    // citations but no web results, and must still produce a snapshot.
    const citedChunks = citedChunkIdsInOrder(text);
    if (!toolTexts && !citedChunks.length && !searchedJson) return "";
    const cited = toolTexts
      ? citedExternalIdsInOrder(
          text,
          collectWebExternalsFromToolTexts(toolTexts),
        )
      : [];
    const value = JSON.stringify([
      toolTexts,
      cited,
      citedChunks,
      searched,
      turnActive,
    ]);
    snapshotCache.current = { text: cacheKey, value };
    return value;
  });

  return useMemo(() => {
    if (!snapshot) return EMPTY;
    const [toolTexts, cited, citedChunks, searched, turnActive] = JSON.parse(
      snapshot,
    ) as [
      string,
      number[],
      number[],
      ProvenanceSearchedEntry[] | undefined,
      boolean | undefined,
    ];
    return {
      externals: collectWebExternalsFromToolTexts(toolTexts),
      citedIdsInOrder: cited,
      queries: collectWebSearchQueriesFromToolTexts(toolTexts),
      citedChunkIds: citedChunks,
      searchedEntries: searched ?? [],
      turnActive: turnActive === true,
    };
  }, [snapshot]);
}
